import {
  useDisclosure,
  useColorModeValue,
  Popover,
  PopoverAnchor,
  Button,
  Portal,
  Tabs,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  PopoverBody,
  SimpleGrid,
  Center,
  Box,
  PopoverTrigger,
  Input,
  IconButton,
  FormControl,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  HStack,
  VStack,
  Heading,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { faHashtag } from '@fortawesome/free-solid-svg-icons';
import { CellContext } from '@tanstack/react-table';
import { unit } from 'mathjs';
import { useMemo } from 'react';
import { HexColorPicker } from 'react-colorful';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import SparkelIcon from '../common/icon/SparkelIcon';
import {
  EditableRowsFormValues,
  EditableRowFormValues,
} from '../project/order-entries-panel/OrderEntriesPanel';
import { TableEntry, isEditableTableMeta, isEditingRow } from './Table';
import { getColorValueFromGradient } from 'src/utils/color-util';
import { COLORS, GRADIENTS } from 'src/utils/row-color-util';
import {
  HatchPatternConfig,
  HatchPatternType,
} from 'src/components/viewers/SheetsViewerV2/shapes/utils/hatch-patterns';
import {
  decodeColorWithPattern,
  encodeColorWithPattern,
} from 'src/utils/color-pattern-encoding';

/**
 * Convert meters to the display unit
 */
function metersToDisplayUnit(meters: number, displayUnit: string): number {
  try {
    const result = unit(meters, 'm').to(displayUnit);
    return result.toNumber(displayUnit);
  } catch {
    return meters;
  }
}

/**
 * Convert from display unit to meters
 */
function displayUnitToMeters(value: number, displayUnit: string): number {
  try {
    const result = unit(value, displayUnit).to('m');
    return result.toNumber('m');
  } catch {
    return value;
  }
}

/**
 * Popover for configuring hatch patterns on order entries
 */
// eslint-disable-next-line complexity
function HatchPatternPopover({
  pattern,
  onPatternChange,
  lengthUnit = 'm',
}: {
  pattern: HatchPatternConfig | null;
  onPatternChange: (pattern: HatchPatternConfig | null) => void;
  lengthUnit?: string;
}) {
  const { t } = useTranslation('table');
  const labelColor = useColorModeValue('gray.500', 'gray.400');
  const activeIconColor = useColorModeValue('teal.500', 'teal.200');
  const activeBackgroundColor = useColorModeValue('teal.50', undefined);
  const {
    isOpen: isPopoverOpen,
    onOpen: onPopoverOpen,
    onClose: onPopoverClose,
  } = useDisclosure();

  const currentPattern = pattern ?? {
    type: HatchPatternType.None,
    angle: 45,
    gap: 0.5, // 0.5 meters default
    lineWidth: 0.02,
  };

  const hasPattern = pattern !== null && pattern.type !== HatchPatternType.None;

  // Convert gap from meters (stored) to display unit for UI
  const gapInDisplayUnit = metersToDisplayUnit(currentPattern.gap, lengthUnit);

  const updatePattern = (updates: Partial<HatchPatternConfig>) => {
    const newPattern = { ...currentPattern, ...updates };
    if (newPattern.type === HatchPatternType.None) {
      onPatternChange(null);
    } else {
      onPatternChange(newPattern);
    }
  };

  // Update gap by converting from display unit to meters
  const updateGap = (displayValue: number) => {
    const metersValue = displayUnitToMeters(displayValue, lengthUnit);
    updatePattern({ gap: metersValue });
  };

  return (
    <Popover
      isLazy
      isOpen={isPopoverOpen}
      onClose={onPopoverClose}
      placement="top"
    >
      <PopoverTrigger>
        <Box>
          <Tooltip
            label={t('color-picker.hatch-pattern.tooltip', 'Hatch pattern')}
            placement="top"
            hasArrow
          >
            <IconButton
              aria-label={t(
                'color-picker.hatch-pattern.tooltip',
                'Hatch pattern'
              )}
              icon={
                <SparkelIcon
                  icon={faHashtag}
                  color={hasPattern ? activeIconColor : 'inherit'}
                />
              }
              size="xs"
              variant="ghost"
              colorScheme="teal"
              borderRadius="md"
              backgroundColor={hasPattern ? activeBackgroundColor : undefined}
              onClick={onPopoverOpen}
            />
          </Tooltip>
        </Box>
      </PopoverTrigger>
      <PopoverContent width="240px">
        <PopoverArrow />
        <PopoverCloseButton size="sm" />
        <PopoverBody>
          <Heading as="h3" size="xs" marginY={1}>
            {t('color-picker.hatch-pattern.title', 'Hatch Pattern')}
          </Heading>
          <VStack spacing={2} align="stretch" marginTop={2}>
            <FormControl>
              <Text fontSize="xs" color={labelColor} marginBottom={1}>
                {t('color-picker.hatch-pattern.type', 'Pattern Type')}
              </Text>
              <Select
                size="xs"
                variant="filled"
                borderRadius="md"
                value={currentPattern.type}
                onChange={(e) =>
                  updatePattern({ type: e.target.value as HatchPatternType })
                }
              >
                <option value={HatchPatternType.None}>
                  {t('color-picker.hatch-pattern.type-none', 'None')}
                </option>
                <option value={HatchPatternType.DiagonalLines}>
                  {t(
                    'color-picker.hatch-pattern.type-diagonal',
                    'Diagonal Lines'
                  )}
                </option>
                <option value={HatchPatternType.CrossHatch}>
                  {t(
                    'color-picker.hatch-pattern.type-crosshatch',
                    'Cross Hatch'
                  )}
                </option>
              </Select>
            </FormControl>

            {currentPattern.type !== HatchPatternType.None && (
              <>
                <FormControl>
                  <Text fontSize="xs" color={labelColor} marginBottom={1}>
                    {t('color-picker.hatch-pattern.angle', 'Angle (degrees)')}
                  </Text>
                  <NumberInput
                    size="xs"
                    variant="filled"
                    min={0}
                    max={180}
                    value={currentPattern.angle}
                    onChange={(_, val) =>
                      updatePattern({ angle: isNaN(val) ? 0 : val })
                    }
                  >
                    <NumberInputField borderRadius="md" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                {currentPattern.type === HatchPatternType.CrossHatch && (
                  <FormControl>
                    <Text fontSize="xs" color={labelColor} marginBottom={1}>
                      {t(
                        'color-picker.hatch-pattern.second-angle',
                        'Second Angle'
                      )}
                    </Text>
                    <NumberInput
                      size="xs"
                      variant="filled"
                      min={0}
                      max={180}
                      value={
                        currentPattern.secondAngle ?? currentPattern.angle + 90
                      }
                      onChange={(_, val) =>
                        updatePattern({ secondAngle: isNaN(val) ? 90 : val })
                      }
                    >
                      <NumberInputField borderRadius="md" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                )}

                <FormControl>
                  <Text fontSize="xs" color={labelColor} marginBottom={1}>
                    {t('color-picker.hatch-pattern.gap', 'Gap')}
                  </Text>
                  <HStack spacing={1}>
                    <NumberInput
                      size="xs"
                      variant="filled"
                      min={0.01}
                      step={0.1}
                      precision={2}
                      value={gapInDisplayUnit}
                      onChange={(_, val) => updateGap(isNaN(val) ? 0.5 : val)}
                      flex={1}
                    >
                      <NumberInputField borderRadius="md" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <Text
                      fontSize="xs"
                      color={labelColor}
                      fontWeight="medium"
                      minWidth="20px"
                    >
                      {lengthUnit}
                    </Text>
                  </HStack>
                </FormControl>
              </>
            )}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

export function ColorPicker<D extends TableEntry>({
  getValue,
  column,
  row,
  table: {
    options: { meta },
  },
}: CellContext<D, string>): React.ReactElement {
  const { setValue, watch } = useFormContext<EditableRowsFormValues>();
  const columnId = column.id as keyof EditableRowFormValues;
  const encodedColor = watch(`${row.id}.${columnId}`) as string;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isCustomColorOpen,
    onOpen: onCustomColorOpen,
    onClose: onCustomColorClose,
  } = useDisclosure();
  const editingOtherColor = useColorModeValue('gray.200', 'gray.600');
  invariant(isEditableTableMeta(meta), 'Table meta is not defined');

  // Decode the color to get the base color and pattern
  const { color: currentColor, pattern: hatchPattern } = useMemo(
    () => decodeColorWithPattern(encodedColor),
    [encodedColor]
  );

  // Handler to update color while preserving pattern
  const setColorValue = (newColor: string) => {
    const encoded = encodeColorWithPattern(newColor, hatchPattern);
    setValue(`${row.id}.${columnId}`, encoded);
  };

  // Handler to update pattern while preserving color
  const setHatchPattern = (newPattern: HatchPatternConfig | null) => {
    const encoded = encodeColorWithPattern(currentColor, newPattern);
    setValue(`${row.id}.${columnId}`, encoded);
  };

  const { t } = useTranslation('table');

  if (isEditingRow(row, meta)) {
    return (
      <Popover
        isLazy
        isOpen={isOpen}
        onClose={() => {
          onClose();
          onCustomColorClose();
        }}
      >
        <PopoverAnchor>
          <Button
            aria-label="color"
            variant="unstyled"
            _hover={{ opacity: '0.6' }}
            height="16px"
            width="16px"
            padding={0}
            minWidth="unset"
            borderRadius="full"
            background={currentColor}
            onClick={onOpen}
          />
        </PopoverAnchor>
        <Portal>
          <Tabs
            variant="panel-tabs"
            isLazy
            size={'sm'}
            defaultIndex={currentColor?.includes('gradient') ? 1 : 0}
          >
            <PopoverContent maxWidth="210">
              <PopoverArrow />
              <PopoverCloseButton marginTop={1} />
              <PopoverHeader>
                <HStack spacing={2} align="center">
                  <TabList>
                    <Tab>{t('color-picker.tab.color')}</Tab>
                    <Tab>{t('color-picker.tab.gradient')}</Tab>
                  </TabList>
                  <HatchPatternPopover
                    pattern={hatchPattern}
                    onPatternChange={setHatchPattern}
                    lengthUnit={meta.lengthUnit}
                  />
                </HStack>
              </PopoverHeader>
              <TabPanels>
                <TabPanel>
                  <PopoverBody>
                    <SimpleGrid alignItems="center" columns={4} spacing={2}>
                      {COLORS.map((c) => (
                        <Center key={c}>
                          <Button
                            aria-label={c}
                            background={c}
                            height={6}
                            width={6}
                            padding={0}
                            minWidth="unset"
                            borderRadius="full"
                            outline={
                              currentColor === c ? `2px solid ${c}` : undefined
                            }
                            outlineOffset={3}
                            _hover={{ background: c }}
                            onClick={() => {
                              setColorValue(c);
                              onClose();
                            }}
                          />
                        </Center>
                      ))}
                      <Popover
                        isLazy
                        isOpen={isCustomColorOpen}
                        onClose={onCustomColorClose}
                      >
                        <PopoverTrigger>
                          <Input
                            gridColumn="span 4"
                            variant="filled"
                            value={currentColor}
                            onChange={(e) => {
                              setColorValue(e.target.value);
                            }}
                            size="xs"
                            borderRadius={'md'}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!isCustomColorOpen) {
                                onCustomColorOpen();
                                // Keep focus on input
                                e.currentTarget.focus();
                              }
                            }}
                            onFocus={(e) => e.target.select()}
                          />
                        </PopoverTrigger>
                        <PopoverContent
                          width="220px"
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          <PopoverBody padding={4}>
                            <HexColorPicker
                              color={currentColor}
                              onChange={(color) => {
                                setColorValue(color);
                              }}
                            />
                          </PopoverBody>
                        </PopoverContent>
                      </Popover>
                    </SimpleGrid>
                  </PopoverBody>
                </TabPanel>
                <TabPanel>
                  <PopoverBody>
                    <SimpleGrid alignItems="center" columns={4} spacing={2}>
                      {GRADIENTS.map((gradient) => (
                        <Center key={gradient}>
                          <Button
                            background={gradient}
                            _hover={{
                              background: gradient,
                            }}
                            aria-label={gradient}
                            height={6}
                            width={6}
                            padding={0}
                            minWidth="unset"
                            borderRadius="full"
                            outline={
                              currentColor === gradient
                                ? `2px solid ${getColorValueFromGradient(
                                    gradient,
                                    0.5
                                  )}`
                                : undefined
                            }
                            outlineOffset={3}
                            onClick={() => {
                              setColorValue(gradient);
                              onClose();
                            }}
                          />
                        </Center>
                      ))}
                    </SimpleGrid>
                  </PopoverBody>
                </TabPanel>
              </TabPanels>
            </PopoverContent>
          </Tabs>
        </Portal>
      </Popover>
    );
  } else {
    const isEditingOtherRow = meta.editingRows.some(
      (editingRow) => editingRow !== row.id
    );
    // Extract color and pattern for display
    const { color: displayColor, pattern } = decodeColorWithPattern(getValue());
    const size = row.depth === 0 ? 4 : 2.5;
    const sizeInPx = row.depth === 0 ? 16 : 10;

    // Generate pattern indicator lines based on pattern type and angle
    const getPatternIndicator = () => {
      if (!pattern || pattern.type === HatchPatternType.None) return null;

      const angle = pattern.angle ?? 45;
      const angleRad = (angle * Math.PI) / 180;
      const center = sizeInPx / 2;
      const lineLength = sizeInPx * 0.9;
      const halfLen = lineLength / 2;

      // Calculate line endpoints based on angle
      const dx = Math.cos(angleRad) * halfLen;
      const dy = Math.sin(angleRad) * halfLen;

      const lines = [
        // Primary line
        <line
          key="primary"
          x1={center - dx}
          y1={center + dy}
          x2={center + dx}
          y2={center - dy}
          stroke="rgba(0,0,0,0.3)"
          strokeWidth={1.5}
        />,
      ];

      // Add second line for crosshatch
      if (pattern.type === HatchPatternType.CrossHatch) {
        const secondAngle = pattern.secondAngle ?? angle + 90;
        const secondRad = (secondAngle * Math.PI) / 180;
        const dx2 = Math.cos(secondRad) * halfLen;
        const dy2 = Math.sin(secondRad) * halfLen;
        lines.push(
          <line
            key="secondary"
            x1={center - dx2}
            y1={center + dy2}
            x2={center + dx2}
            y2={center - dy2}
            stroke="rgba(0,0,0,0.3)"
            strokeWidth={1.5}
          />
        );
      }

      return (
        <svg
          width={sizeInPx}
          height={sizeInPx}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            pointerEvents: 'none',
          }}
        >
          {lines}
        </svg>
      );
    };

    return (
      <Box
        position="relative"
        height={size}
        width={size}
        marginLeft={row.depth === 0 ? 0 : '3px'}
        padding={0}
        minWidth="unset"
        borderRadius="full"
        background={isEditingOtherRow ? editingOtherColor : displayColor}
        overflow="hidden"
      >
        {!isEditingOtherRow && getPatternIndicator()}
      </Box>
    );
  }
}
